import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../services/axiosInstance";

export const fetchCrmStats = createAsyncThunk("crm/fetchCrmStats", async (_, thunkAPI) => {
  try {
    const res = await axiosInstance.get("/crm/stats");
    return res.data;
  } catch { return thunkAPI.rejectWithValue(null); }
});

export const fetchOpenCount = createAsyncThunk("crm/fetchOpenCount", async (_, thunkAPI) => {
  try {
    const res = await axiosInstance.get("/crm/tickets?status=open&limit=1");
    return res.data.total ?? 0;
  } catch {
    return 0;
  }
});

export const fetchTickets = createAsyncThunk("crm/fetchTickets", async (params = {}, thunkAPI) => {
  try {
    const res = await axiosInstance.get("/crm/tickets", { params });
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const createTicket = createAsyncThunk("crm/createTicket", async (body, thunkAPI) => {
  try {
    const res = await axiosInstance.post("/crm/tickets", body);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const fetchTicketDetail = createAsyncThunk("crm/fetchTicketDetail", async (id, thunkAPI) => {
  try {
    const res = await axiosInstance.get(`/crm/tickets/${id}`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const patchTicket = createAsyncThunk("crm/patchTicket", async ({ id, ...body }, thunkAPI) => {
  try {
    await axiosInstance.patch(`/crm/tickets/${id}`, body);
    return { id, ...body };
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const addNote = createAsyncThunk("crm/addNote", async ({ ticketId, ...body }, thunkAPI) => {
  try {
    const res = await axiosInstance.post(`/crm/tickets/${ticketId}/notes`, body);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const deleteNote = createAsyncThunk("crm/deleteNote", async ({ ticketId, noteId }, thunkAPI) => {
  try {
    await axiosInstance.delete(`/crm/tickets/${ticketId}/notes/${noteId}`);
    return noteId;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const addTask = createAsyncThunk("crm/addTask", async ({ ticketId, ...body }, thunkAPI) => {
  try {
    const res = await axiosInstance.post(`/crm/tickets/${ticketId}/tasks`, body);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const toggleTask = createAsyncThunk("crm/toggleTask", async ({ ticketId, taskId, status }, thunkAPI) => {
  try {
    await axiosInstance.patch(`/crm/tickets/${ticketId}/tasks/${taskId}`, { status });
    return { taskId, status };
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const bulkPatchStatus = createAsyncThunk("crm/bulkPatchStatus", async ({ ids, status }, thunkAPI) => {
  try {
    await axiosInstance.post("/crm/tickets/bulk-status", { ids, status });
    return { ids, status };
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const fetchUrgentTickets = createAsyncThunk("crm/fetchUrgentTickets", async (_, thunkAPI) => {
  try {
    const res = await axiosInstance.get("/crm/tickets", { params: { status: "open", limit: 10 } });
    return res.data;
  } catch { return thunkAPI.rejectWithValue(null); }
});

export const bulkPatchAssignee = createAsyncThunk("crm/bulkPatchAssignee", async ({ ids, assignedToId, assigneeName, assigneeLastName }, thunkAPI) => {
  try {
    await axiosInstance.post("/crm/tickets/bulk-assign", { ids, assignedToId });
    return { ids, assignedToId, assigneeName, assigneeLastName };
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

export const deleteTask = createAsyncThunk("crm/deleteTask", async ({ ticketId, taskId }, thunkAPI) => {
  try {
    await axiosInstance.delete(`/crm/tickets/${ticketId}/tasks/${taskId}`);
    return taskId;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.error || err.message);
  }
});

const crmSlice = createSlice({
  name: "crm",
  initialState: {
    tickets: [],
    total: 0,
    openCount: 0,
    urgentTickets: [],
    urgentStatus: "idle",
    stats: null,
    selected: null,
    notes: [],
    tasks: [],
    status: "idle",
    detailStatus: "idle",
    filters: { status: "", priority: "", search: "", dateFrom: "", dateTo: "", sortBy: "", sortDir: "desc", overdue: "", searchNotes: "", assignedToId: "" },
  },
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelected(state) {
      state.selected = null;
      state.notes = [];
      state.tasks = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCrmStats.fulfilled, (s, a) => { s.stats = a.payload; })

      .addCase(fetchOpenCount.fulfilled, (s, a) => { s.openCount = a.payload; })

      .addCase(fetchTickets.pending,   (s) => { s.status = "loading"; })
      .addCase(fetchTickets.fulfilled, (s, a) => { s.status = "idle"; s.tickets = a.payload.tickets; s.total = a.payload.total; })
      .addCase(fetchTickets.rejected,  (s) => { s.status = "error"; })

      .addCase(fetchTicketDetail.pending,   (s) => { s.detailStatus = "loading"; })
      .addCase(fetchTicketDetail.fulfilled, (s, a) => {
        s.detailStatus = "idle";
        const { notes, tasks, ...ticket } = a.payload;
        s.selected = ticket;
        s.notes = notes;
        s.tasks = tasks;
      })
      .addCase(fetchTicketDetail.rejected, (s) => { s.detailStatus = "error"; })

      .addCase(patchTicket.fulfilled, (s, a) => {
        const { id, ...changes } = a.payload;
        const t = s.tickets.find((t) => t.id === id);
        if (t) Object.assign(t, changes);
        if (s.selected?.id === id) Object.assign(s.selected, changes);
      })

      .addCase(addNote.fulfilled,    (s, a) => { s.notes.push(a.payload); })
      .addCase(deleteNote.fulfilled, (s, a) => { s.notes = s.notes.filter((n) => n.id !== a.payload); })

      .addCase(addTask.fulfilled, (s, a) => { s.tasks.push(a.payload); })
      .addCase(toggleTask.fulfilled, (s, a) => {
        const t = s.tasks.find((t) => t.id === a.payload.taskId);
        if (t) t.status = a.payload.status;
      })
      .addCase(deleteTask.fulfilled, (s, a) => { s.tasks = s.tasks.filter((t) => t.id !== a.payload); })

      .addCase(createTicket.fulfilled, (s) => { s.status = "idle"; })

      .addCase(fetchUrgentTickets.pending,   (s) => { s.urgentStatus = "loading"; })
      .addCase(fetchUrgentTickets.fulfilled, (s, a) => {
        s.urgentStatus = "idle";
        s.urgentTickets = a.payload?.tickets ?? [];
        if (a.payload?.total != null) s.openCount = a.payload.total;
      })
      .addCase(fetchUrgentTickets.rejected, (s) => { s.urgentStatus = "error"; })

      .addCase(bulkPatchStatus.fulfilled, (s, a) => {
        const { ids, status } = a.payload;
        const idSet = new Set(ids);
        s.tickets.forEach((t) => { if (idSet.has(t.id)) t.status = status; });
        if (s.selected && idSet.has(s.selected.id)) s.selected.status = status;
      })

      .addCase(bulkPatchAssignee.fulfilled, (s, a) => {
        const { ids, assignedToId, assigneeName, assigneeLastName } = a.payload;
        const idSet = new Set(ids);
        s.tickets.forEach((t) => {
          if (idSet.has(t.id)) {
            t.assignedToId = assignedToId ?? null;
            t.assigneeName = assigneeName ?? null;
            t.assigneeLastName = assigneeLastName ?? null;
          }
        });
        if (s.selected && idSet.has(s.selected.id)) {
          s.selected.assignedToId = assignedToId ?? null;
          s.selected.assigneeName = assigneeName ?? null;
          s.selected.assigneeLastName = assigneeLastName ?? null;
        }
      });
  },
});

export const { setFilters, clearSelected } = crmSlice.actions;
export default crmSlice.reducer;
